import type { CalendarEvent } from '../types';

import { minutesFromDayStart } from './dateGrid';
import { minutesToPx } from './grid';

export interface PositionedEvent {
  event: CalendarEvent;
  laneIndex: number;
  laneCount: number;
  topPx: number;
  heightPx: number;
}

interface PackEventsOptions {
  pxPerMinute: number;
  dayStart: Date;
  timeZone: string;
}

function sortForPacking(a: CalendarEvent, b: CalendarEvent): number {
  if (a.start.getTime() !== b.start.getTime()) return a.start.getTime() - b.start.getTime();
  const aDuration = a.end.getTime() - a.start.getTime();
  const bDuration = b.end.getTime() - b.start.getTime();
  return bDuration - aDuration;
}

function assignLanes(cluster: CalendarEvent[]): Map<string, number> {
  const lanes = greedyAssignLanes(
    cluster,
    (event) => event.start.getTime(),
    (event) => event.end.getTime(),
    // Continuous time: a block starting exactly when the previous ends doesn't overlap it.
    (laneEnd, start) => laneEnd <= start
  );
  return new Map(cluster.map((event, i) => [event.id, lanes[i]]));
}

/** Greedily assigns each of `sortedItems` (already sorted by start) to the
 * earliest lane whose current occupant `canReuseLane` accepts, tracking each
 * lane's occupant by its end value. Returns lane indices in input order.
 * `canReuseLane` encodes the domain's adjacency rule (e.g. `<=` for
 * continuous time vs. `<` for inclusive integer columns), so callers with
 * different units/semantics can share this loop without drifting apart. */
export function greedyAssignLanes<T>(
  sortedItems: T[],
  getStart: (item: T) => number,
  getEnd: (item: T) => number,
  canReuseLane: (laneEnd: number, start: number) => boolean
): number[] {
  const laneEnds: number[] = [];
  return sortedItems.map((item) => {
    const start = getStart(item);
    let lane = laneEnds.findIndex((end) => canReuseLane(end, start));
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(getEnd(item));
    } else {
      laneEnds[lane] = getEnd(item);
    }
    return lane;
  });
}

/** Packs `events` into non-overlapping lanes. Caller is responsible for splitting foreground/background lists first. */
export function packEvents(events: CalendarEvent[], opts: PackEventsOptions): PositionedEvent[] {
  const { pxPerMinute, dayStart, timeZone } = opts;
  const sorted = [...events].sort(sortForPacking);

  const positioned: PositionedEvent[] = [];
  let cluster: CalendarEvent[] = [];
  let clusterMaxEnd = -Infinity;

  const flushCluster = () => {
    if (cluster.length === 0) return;
    const laneIndexById = assignLanes(cluster);
    const laneCount = new Set(laneIndexById.values()).size;
    for (const event of cluster) {
      const topPx = minutesToPx(minutesFromDayStart(event.start, dayStart, timeZone), pxPerMinute);
      const heightPx = minutesToPx(
        (event.end.getTime() - event.start.getTime()) / 60_000,
        pxPerMinute
      );
      positioned.push({
        event,
        laneIndex: laneIndexById.get(event.id) ?? 0,
        laneCount,
        topPx,
        heightPx
      });
    }
    cluster = [];
    clusterMaxEnd = -Infinity;
  };

  for (const event of sorted) {
    if (cluster.length > 0 && event.start.getTime() >= clusterMaxEnd) {
      flushCluster();
    }
    cluster.push(event);
    clusterMaxEnd = Math.max(clusterMaxEnd, event.end.getTime());
  }
  flushCluster();

  return positioned;
}
